import Chart from 'chart.js';
import { useEffect, useState } from 'react';

// Chart.js draws to canvas, so the CSS token overrides in theme.css cannot reach it.
// Light mirrors the Chart.js v2 stock values so light-mode charts render exactly as
// before; dark follows the --admin-* palette.
const CHART_THEME_COLORS = {
  light: {
    fontColor: '#666',
    gridLineColor: 'rgba(0, 0, 0, 0.1)',
    zeroLineColor: 'rgba(0, 0, 0, 0.25)',
  },
  dark: {
    // fontColor is a fallback for when --admin-text-muted is unavailable
    fontColor: '#94a3b8',
    gridLineColor: 'rgba(148, 163, 184, 0.15)',
    zeroLineColor: 'rgba(148, 163, 184, 0.3)',
  },
};

export const getAdminChartTheme = () => (document.body.dataset.adminTheme === 'dark' ? 'dark' : 'light');

// Sync the Chart.js global defaults (axis/legend/title text, gridlines) with the
// active admin theme. Call right before creating a chart: gridline colors are
// resolved at chart creation, font colors at draw time.
export const applyChartThemeDefaults = () => {
  const theme = getAdminChartTheme();
  const themeColors = CHART_THEME_COLORS[theme];
  const tokenFontColor =
    theme === 'dark' ? getComputedStyle(document.body).getPropertyValue('--admin-text-muted').trim() : '';
  Chart.defaults.global.defaultFontColor = tokenFontColor || themeColors.fontColor;
  Chart.defaults.scale.gridLines.color = themeColors.gridLineColor;
  Chart.defaults.scale.gridLines.zeroLineColor = themeColors.zeroLineColor;
};

// Charts must be re-created to pick up new defaults: expose the current theme and
// update it when the topbar toggle flips the body attribute.
export const useAdminChartTheme = () => {
  const [theme, setTheme] = useState(getAdminChartTheme);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(getAdminChartTheme()));
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-admin-theme'] });
    return () => observer.disconnect();
  }, []);

  return theme;
};
